import React, { useEffect, useState } from "react";
import { ThemeProvider, DEFAULT_THEME } from "@zendeskgarden/react-theming";
import { Alert } from "@zendeskgarden/react-notifications";
import { Button } from "@zendeskgarden/react-buttons";
import BackendApi from "api/backendApi";
import { checkUserAuthStatus } from "utils/tools";

const Authorization = () => {
  const [isAuthorised, setIsAuthorised] = useState(false);
  const [syncError, setSyncError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [zendeskSubdomain, setZendeskSubdomain] = useState("");
  const [appUserId, setAppUserId] = useState("");
  const [appRandomKey, setAppRandomKey] = useState(0);

  const handleAuthorizeButtonClick = async (appUserId) => {
    if (!appUserId) {
      setSyncError(true);
      return false;
    }

    try {
      let authHandlingRes = await BackendApi.oauthRedirectUser(appUserId);
      console.log("Response from /oauth/redirectUser api request:", authHandlingRes);

      if (
        !!authHandlingRes &&
        authHandlingRes.code == 200 &&
        !!authHandlingRes.data &&
        !!authHandlingRes.data.userRedirectUrl
      ) {
        return window.open(authHandlingRes.data.userRedirectUrl, "_blank");
      }
    } catch (error) {
      console.error("Error making /oauth/redirectUser api request:", error);
      setSyncError(true);
      return false;
    }
  };

  const handleDeauthorizeButtonClick = async (appUserId) => {
    if (!appUserId) {
      setSyncError(true);
      return false;
    }
    setIsLoading(true);

    try {
      let deauthHandlingRes = await BackendApi.oauthDeauthorize(appUserId);
      console.log("Response from /oauth/deauthorize api request:", deauthHandlingRes);

      if (!!deauthHandlingRes && deauthHandlingRes.code == 200) {
        console.log("Deauthorize success");
        client.invoke(
          "notify",
          "Deauthorize success. To use this app again, please ReAuthorize. To uninstall, refer to the install/uninstall instructions.",
          "notice",
          { sticky: true },
        );
        setIsAuthorised(false);
      }
    } catch (error) {
      console.error("Error making /oauth/deauthorize api request:", error);
      setSyncError(true);
    }

    setIsLoading(false);
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setSyncError(false);
      setIsAuthorised(false);
      client.invoke("resize", { width: "100%", height: "100%" });

      let appContextResponse = {};
      try {
        appContextResponse = await client.context();
        console.log("appContextResponse", appContextResponse);
      } catch (error) {
        setIsLoading(false);
        setSyncError(true);
        throw error;
      }

      let appMetadataResponse = {};
      try {
        appMetadataResponse = await client.metadata();
        console.log("appMetadataResponse", appMetadataResponse);
      } catch (error) {
        setIsLoading(false);
        setSyncError(true);
        throw error;
      }

      //instances are transient in some locations (like ticket sidebar), so if the ticket tab is closed that instance is destroyed, so we will not use instanceGuid
      const account = appContextResponse["account"];

      if (!account || !account.subdomain) {
        setIsLoading(false);
        setSyncError(true);
        throw new Error("can not get the zendesk subdomain");
      }

      let appId = appMetadataResponse.appId;
      let installationId = appMetadataResponse.installationId;

      if (!appId || !installationId) {
        setIsLoading(false);
        setSyncError(true);
        throw new Error("can not get the zendesk app metadata");
      }

      setZendeskSubdomain(account.subdomain);

      //Call backend api to check the user authorisation status
      const authStatusQueryData = {
        subdomain: account.subdomain,
        appid: appId,
        installationid: installationId,
      };
      console.log("authStatusQueryData", authStatusQueryData);

      let authStatusDetailData = await checkUserAuthStatus(authStatusQueryData);
      console.log("authStatusDetailData", authStatusDetailData);

      if (!!authStatusDetailData) {
        setIsAuthorised(authStatusDetailData.isAuthorised);
        setAppUserId(authStatusDetailData.uuid);
      } else {
        setSyncError(true);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setSyncError(true);
      console.error("fetchData error:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [client, appRandomKey]);

  // useEffect(() => {
  //   const handleVisibilityChange = () => {
  //     if (document.visibilityState === 'visible') {
  //       // Refresh the app when the tab becomes visible, mainly used for Oauth2 authorization
  //       console.log("app refresh");
  //       setAppRandomKey(Math.random);
  //     }
  //   };
  //
  //   document.addEventListener('visibilitychange', handleVisibilityChange);
  //
  //   return () => {
  //     document.removeEventListener('visibilitychange', handleVisibilityChange);
  //   };
  // }, []);

  useEffect(() => {
    // This code runs after the component mounts
    client.on("api_notification.authorizationSuccess", (data) => {
      // console.log("event authorizationSuccess",data.body, data.sender);
      client.invoke("notify", data.body, "notice", { sticky: true });
      setAppRandomKey(Math.random);
    });

    // Cleanup function to remove the listener when the component unmounts
    return () => {
      client.off("api_notification.authorizationSuccess");
    };
  }, [client]); // Dependency array ensures this runs when `client` changes

  return (
    <ThemeProvider theme={{ ...DEFAULT_THEME }}>
      <div>
        {!!isLoading ? (
          <p style={{ marginLeft: "15px", color: "LightSkyBlue" }}>Loading data, please wait...</p>
        ) : !!syncError ? (
          <Alert type="error">
            <Alert.Title>Error</Alert.Title>
            An error occurred, please reload the app!
          </Alert>
        ) : !isAuthorised ? (
          <>
            <div className="tab-pannel flex flex-col items-center justify-center mt-12">
              <div className="text-lg font-semibold mb-7">API Access</div>
              <div className="text-sm mb-5">Please authorize API access.</div>
              <div>
                <Button style={{ width: "200px" }} onClick={() => handleAuthorizeButtonClick(appUserId)}>
                  Authorize API
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="tab-pannel flex flex-col items-center justify-center mt-12">
              <div className="text-lg font-semibold mb-7">API Access</div>
              {/* <div className="text-sm mb-5">Please authorize API access.</div> */}
              <div>
                <Button style={{ width: "200px" }} onClick={() => handleDeauthorizeButtonClick(appUserId)}>
                  Deauthorize API
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </ThemeProvider>
  );
};

export default Authorization;
